import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Inbox, Sparkles, Send, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const CARDS = [
  { key: "new", label: "New Leads", Icon: Inbox, color: "text-teal-600", bg: "bg-sage/40" },
  { key: "contacted", label: "Contacted", Icon: Send, color: "text-gold", bg: "bg-gold/10" },
  { key: "proposal_sent", label: "Proposal Sent", Icon: Sparkles, color: "text-charcoal", bg: "bg-beige" },
  { key: "converted", label: "Converted", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "dropped", label: "Dropped", Icon: Inbox, color: "text-rose-700", bg: "bg-rose-50" },
];

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([api.get("/leads/stats"), api.get("/leads")])
      .then(([s, r]) => {
        if (!active) return;
        setStats(s.data);
        setRecent((r.data || []).slice(0, 5));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="admin-overview-page">
      <header className="mb-8 md:mb-10">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Studio</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Welcome back.</h1>
        <p className="text-charcoal/65 font-light mt-2 max-w-xl">Here&apos;s a quick look at your lead pipeline and recent enquiries.</p>
      </header>

      {/* Stat row */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
        <StatCard label="Total Leads" value={stats?.total ?? "—"} loading={loading} accent />
        {CARDS.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            value={stats?.by_status?.[c.key] ?? "—"}
            Icon={c.Icon}
            color={c.color}
            bg={c.bg}
            loading={loading}
            testid={`stat-${c.key}`}
          />
        ))}
      </section>

      {/* Recent leads */}
      <section className="bg-white border border-beige" data-testid="admin-recent-leads">
        <div className="flex items-center justify-between px-5 md:px-7 py-5 border-b border-beige">
          <h2 className="font-display text-xl text-charcoal">Recent enquiries</h2>
          <Link to="/admin/leads" className="text-[11px] uppercase tracking-[0.22em] text-teal-600 hover:text-charcoal flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <ul className="divide-y divide-beige">
          {loading ? (
            <li className="p-7 text-charcoal/40">Loading…</li>
          ) : recent.length === 0 ? (
            <li className="p-7 text-charcoal/40 italic font-light">No leads yet. They&apos;ll appear here as enquiries come in.</li>
          ) : (
            recent.map((l) => (
              <li key={l.id} className="grid grid-cols-12 gap-4 px-5 md:px-7 py-5 items-center hover:bg-sage/20 transition-colors">
                <div className="col-span-12 md:col-span-4">
                  <div className="font-display text-lg text-charcoal">{l.name}</div>
                  <div className="text-sm text-charcoal/55">{l.email} · {l.phone}</div>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-charcoal/70">{l.wedding_location || "—"}</div>
                <div className="col-span-6 md:col-span-3 text-sm text-charcoal/70">{l.wedding_date || "—"}</div>
                <div className="col-span-12 md:col-span-2 text-right">
                  <StatusBadge status={l.status} />
                </div>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}

function StatCard({ label, value, Icon, color, bg, loading, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative bg-white border border-beige p-5 md:p-6 ${accent ? "md:col-span-1" : ""}`}
    >
      {Icon ? <Icon className={`absolute top-5 right-5 h-4 w-4 ${color}`} strokeWidth={1.4} /> : null}
      <div className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55 mb-3">{label}</div>
      <div className={`font-display text-3xl md:text-4xl ${accent ? "text-teal-600" : "text-charcoal"} ${loading ? "animate-pulse" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: { label: "New", cls: "bg-teal-600/10 text-teal-700" },
    contacted: { label: "Contacted", cls: "bg-gold/15 text-gold-600" },
    proposal_sent: { label: "Proposal", cls: "bg-beige text-charcoal" },
    converted: { label: "Converted", cls: "bg-emerald-100 text-emerald-700" },
    dropped: { label: "Dropped", cls: "bg-rose-100 text-rose-700" },
  };
  const s = map[status] || map.new;
  return <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-medium ${s.cls}`}>{s.label}</span>;
}
